/**
 * Tracking Bridge — connects job lifecycle events to the location tracking system.
 * 
 * Dispatches custom DOM events that the DriverLocationTracker component listens for
 * to start/stop GPS tracking tied to specific jobs.
 * 
 * In a Capacitor native context, this also posts messages to the WebView bridge
 * for native background tracking coordination.
 */

import { Capacitor } from "@capacitor/core";

export interface TrackingEvent {
  type: "START_TRACKING" | "STOP_TRACKING";
  jobId: string;
  timestamp: number;
}

/**
 * Signal that tracking should start for a job (POC begins or tracking_shared enabled).
 */
export function startJobTracking(jobId: string): void {
  const event: TrackingEvent = {
    type: "START_TRACKING",
    jobId,
    timestamp: Date.now(),
  };
  console.log(`[TrackingBridge] START_TRACKING jobId=${jobId}`);
  
  // Dispatch DOM event for React components (DriverLocationTracker)
  window.dispatchEvent(new CustomEvent("job-tracking-event", { detail: event }));

  // If running inside Capacitor WebView, also post to native layer
  if (Capacitor.isNativePlatform()) {
    try {
      (window as any).Capacitor?.Plugins?.WebView?.postMessage?.(JSON.stringify(event));
    } catch {
      // Native bridge not available — DOM event is sufficient
    }
  }
}

/**
 * Signal that tracking should stop for a job (POD completed or logout).
 */
export function stopJobTracking(jobId: string): void {
  const event: TrackingEvent = {
    type: "STOP_TRACKING",
    jobId,
    timestamp: Date.now(),
  };
  console.log(`[TrackingBridge] STOP_TRACKING jobId=${jobId}`);

  window.dispatchEvent(new CustomEvent("job-tracking-event", { detail: event }));

  if (Capacitor.isNativePlatform()) {
    try {
      (window as any).Capacitor?.Plugins?.WebView?.postMessage?.(JSON.stringify(event));
    } catch {
      // Native bridge not available
    }
  }
}

/**
 * Listen for tracking events (used by DriverLocationTracker or native bridge).
 * Returns an unsubscribe function.
 */
export function onTrackingEvent(callback: (event: TrackingEvent) => void): () => void {
  const handler = (e: Event) => {
    const detail = (e as CustomEvent<TrackingEvent>).detail;
    callback(detail);
  };
  window.addEventListener("job-tracking-event", handler);
  return () => window.removeEventListener("job-tracking-event", handler);
}
